import React from 'react';
import { ShieldCheck, Truck, Clock, Facebook, Instagram } from 'lucide-react';
import { SITE_CONFIG } from '../config/siteConfig';

interface FooterProps {
    onOpenLegal?: (type: 'privacy' | 'terms') => void;
    onSelectBrand?: (brandName: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onOpenLegal, onSelectBrand }) => {

    const handleBrandClick = (e: React.MouseEvent, brandName: string) => {
        if (onSelectBrand) {
            e.preventDefault();
            onSelectBrand(brandName);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <footer className="bg-zinc-950 border-t border-zinc-900 pt-20 text-balance">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Trust Signals (Horizontal on Desktop, Stacked on Mobile) */}
                <div id="trust-signals" className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 border-b border-zinc-900 pb-12">
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-zinc-900 rounded border border-zinc-800 flex-shrink-0">
                            <ShieldCheck className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h4 className="text-white font-display font-bold uppercase text-lg">Garantía Premium</h4>
                            <p className="text-zinc-500 text-sm mt-1 leading-relaxed">Todos nuestros productos cuentan con respaldo de fábrica y garantía local de 12 meses.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-zinc-900 rounded border border-zinc-800 flex-shrink-0">
                            <Truck className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h4 className="text-white font-display font-bold uppercase text-lg">Envíos Nacionales</h4>
                            <p className="text-zinc-500 text-sm mt-1 leading-relaxed">Despacho express a todo el país en los 14 departamentos de El Salvador.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="p-3 bg-zinc-900 rounded border border-zinc-800 flex-shrink-0">
                            <Clock className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <h4 className="text-white font-display font-bold uppercase text-lg">Asesoría Técnica</h4>
                            <p className="text-zinc-500 text-sm mt-1 leading-relaxed">Expertos en protección móvil listos para ayudarte a elegir el case correcto vía WhatsApp.</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-8">
                    {/* Brand Info & Socials */}
                    <div className="flex flex-col">
                        <img
                            src={SITE_CONFIG.logo}
                            alt={SITE_CONFIG.brandName}
                            className="h-12 w-auto mb-6 object-contain self-start"
                        />
                        <p className="text-zinc-500 text-sm leading-relaxed mb-6">
                            {SITE_CONFIG.footer.description}
                        </p>

                        <div className="flex gap-4">
                            <a href={SITE_CONFIG.socialMedia.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram de CarbonCase SV" className="w-10 h-10 bg-zinc-900 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors border border-zinc-800">
                                <Instagram className="h-5 w-5" />
                            </a>
                            <a href={SITE_CONFIG.socialMedia.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook de CarbonCase SV" className="w-10 h-10 bg-zinc-900 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors border border-zinc-800">
                                <Facebook className="h-5 w-5" />
                            </a>
                            <a href={SITE_CONFIG.socialMedia.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok de CarbonCase SV" className="w-10 h-10 bg-zinc-900 flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors border border-zinc-800">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Quick Links / Semantic Internal Links */}
                    <div>
                        <h5 className="text-white font-display font-bold uppercase text-sm mb-4 tracking-widest">Catálogo & Marcas</h5>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a
                                    href="/?brand=Apple"
                                    onClick={(e) => handleBrandClick(e, 'Apple')}
                                    className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full" />
                                    Cases de Fibra de Carbono para Apple (iPhone)
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/?brand=Samsung"
                                    onClick={(e) => handleBrandClick(e, 'Samsung')}
                                    className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full" />
                                    Cases de Fibra de Carbono para Samsung Galaxy
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/#catalog-section"
                                    onClick={(e) => handleBrandClick(e, 'Catálogo')}
                                    className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full" />
                                    Ver Todo el Catálogo Disponible
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#trust-signals"
                                    className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 bg-zinc-600 rounded-full" />
                                    Envíos y Garantía en El Salvador
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* SEO / Info Section */}
                    <div>
                        <h5 className="text-white font-display font-bold uppercase text-sm mb-4 tracking-widest">Sobre Nosotros</h5>
                        <p className="text-zinc-500 text-sm leading-relaxed mb-4">
                            En <span className="text-white">Carbon Case SV</span> somos especialistas en la venta de <span className="text-white">carbon case</span> y accesorios de <span className="text-white">fibra de carbono</span> real.
                        </p>
                        <p className="text-zinc-500 text-sm leading-relaxed">
                            Si buscas un <span className="text-white">case fibra de carbono</span> para tu iPhone o Samsung, disponemos de las mejores opciones de <span className="text-white">case sv</span> y <span className="text-white">carbon sv</span> con envíos directos a todo El Salvador.
                        </p>
                    </div>
                </div>
            </div>

            {/* Copyright Bar */}
            <div className="border-t border-zinc-900 bg-black py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-xs text-zinc-600 uppercase tracking-widest text-center md:text-left">
                        © {new Date().getFullYear()} {SITE_CONFIG.brandName}. Todos los derechos reservados.
                    </p>
                    <div className="flex gap-6 text-xs text-zinc-600 uppercase tracking-widest font-bold">
                        <a
                            href="#privacidad"
                            onClick={(e) => {
                                e.preventDefault();
                                onOpenLegal?.('privacy');
                            }}
                            className="hover:text-white transition-colors uppercase"
                        >
                            Privacidad
                        </a>
                        <a
                            href="#terminos"
                            onClick={(e) => {
                                e.preventDefault();
                                onOpenLegal?.('terms');
                            }}
                            className="hover:text-white transition-colors uppercase"
                        >
                            Términos
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
